import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BuyerContact,
  CallOutcome,
  CollectionActivityReference,
  CollectionActivityWithUser,
} from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import {
  updateCollectionActivity,
  getBuyerCollectionActivities,
} from '../../../../../../redux/services/collectionActivityService';
import { DillModal } from '../../../../../general/Dill/DillModal';
import { DillButton } from '../../../../../general/Dill/DillButton';
import { AppInputField } from '../../../../../general/AppInputField/AppInputField';
import { AppDropDown } from '../../../../../general/AppDropDown/AppDropDown';
import { openMessageModal } from '../../../../../../redux/globalSlices/genericSlice';
import LexicalEditor from '../../../../../general/LexicalEditor/LexicalEditor';
import ActivityReferenceSelector from '../../ActivityReferenceSelector/ActivityReferenceSelector';

interface EditCallModalProps {
  open: boolean;
  activity: CollectionActivityWithUser | null;
  onClose: () => void;
  onSuccess?: () => void;
  contacts?: BuyerContact[];
}

const callOutcomeOptions: { value: CallOutcome; label: string }[] = [
  { value: 'REACHED', label: 'Reached - Spoke with contact' },
  { value: 'VOICEMAIL', label: 'Voicemail - Left a message' },
  { value: 'NO_ANSWER', label: 'No Answer - No response' },
  { value: 'BUSY', label: 'Busy - Line was busy' },
  { value: 'FAILED', label: 'Failed - Call could not connect' },
];

function EditCallModal({ open, activity, onClose, onSuccess, contacts }: EditCallModalProps) {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { loadingList } = useAppSelector((state) => state.collectionActivities);

  const [phone, setPhone] = useState('');
  const [duration, setDuration] = useState<number>(0);
  const [outcome, setOutcome] = useState<CallOutcome>('REACHED');
  const [notes, setNotes] = useState('');
  const [references, setReferences] = useState<CollectionActivityReference[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const isLoading = loadingList.includes('updateCollectionActivity');

  // Derive initial notes from activity - memoized
  const initialNotes = useMemo(() => {
    if (!activity?.callDetails?.notes) return undefined;
    const existingNotes = activity.callDetails.notes;
    return typeof existingNotes === 'string' ? existingNotes : JSON.stringify(existingNotes);
  }, [activity?.callDetails?.notes]);

  // Initialize form with existing call details when activity changes
  useEffect(() => {
    if (activity?.callDetails) {
      setPhone(activity.callDetails.phoneNumber || '');
      setDuration(activity.callDetails.duration || 0);
      setOutcome(activity.callDetails.outcome || 'REACHED');
    }
    setReferences(activity?.references || []);
  }, [activity]);

  const handleNotesChange = useCallback((content: string) => {
    setNotes(content);
  }, []);

  const handleSubmit = async () => {
    if (!phone.trim()) {
      setPhoneError('Please enter a phone number');
      return;
    }

    if (!currentSupplier?.id || !activity) {
      return;
    }

    if (!user?.id) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Build edit history entry for notes if they changed
      const existingNotes = activity.callDetails?.notes;
      const notesContent = notes || initialNotes;
      const newNotes = notesContent ? JSON.parse(notesContent) : undefined;

      const notesChanged = JSON.stringify(existingNotes) !== JSON.stringify(newNotes);

      let editHistory = activity.callDetails?.editHistory || [];

      if (notesChanged && existingNotes) {
        const editHistoryEntry = {
          previousContent: existingNotes,
          versionCreatedAt: activity.createdAt || new Date(),
          replacedAt: new Date(),
          replacedBy: user.id,
        };
        editHistory = [...editHistory, editHistoryEntry];
      }

      const result = await dispatch(
        updateCollectionActivity({
          supplierId: currentSupplier.id,
          activityId: activity.id,
          updateData: {
            callDetails: {
              phoneNumber: phone,
              duration,
              outcome,
              notes: newNotes,
              recordingUrl: activity.callDetails?.recordingUrl,
              editHistory,
            },
            references,
          },
        })
      );

      if (result.payload && (result.payload as any).success) {
        // Refresh the activities list
        dispatch(
          getBuyerCollectionActivities({
            supplierId: currentSupplier.id,
            buyerId: activity.buyerId,
          })
        );
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Failed to update call:', error);
      dispatch(
        openMessageModal({
          title: 'Error',
          subTitle: 'Failed to update call. Please try again.',
          buttonText: 'OK',
          modalType: 'ERROR',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title="Edit Phone Call"
      maxWidth="576px"
      footer={
        <>
          <DillButton
            onClick={handleClose}
            variant="secondary"
            isDisabled={isLoading}
            label="Cancel"
          />
          <DillButton
            onClick={handleSubmit}
            variant="primary"
            isLoading={isLoading}
            label="Save Changes"
          />
        </>
      }>
      <AppInputField
        label="Phone Number"
        isRequired
        id="phone"
        value={phone}
        onTextChange={(val) => {
          setPhone(val);
          if (phoneError) setPhoneError('');
        }}
        placeholder="+1 (555) 123-4567"
        isDisabled={isLoading}
        errorText={phoneError}
      />

      <div className="grid grid-cols-2 gap-4">
        <AppInputField
          label="Duration (seconds)"
          id="duration"
          inputType="number"
          value={duration.toString()}
          onTextChange={(val) => setDuration(parseInt(val) || 0)}
          placeholder="0"
          isDisabled={isLoading}
        />
        <AppDropDown
          label="Call Outcome"
          items={callOutcomeOptions}
          value={outcome}
          onSelectChange={(selected) => setOutcome(selected?.value as CallOutcome)}
          isDisabled={isLoading}
          placeholder="Select outcome"
        />
      </div>

      <LexicalEditor
        label="Call Notes (Optional)"
        initialContent={initialNotes}
        onChange={handleNotesChange}
        placeholder="Enter call notes..."
        minHeight="100px"
        editable={!isLoading}
      />

      {activity?.callDetails?.editHistory && activity.callDetails.editHistory.length > 0 && (
        <div className="text-xs text-gray-500">
          Call notes have been edited {activity.callDetails.editHistory.length} time(s)
        </div>
      )}

      <ActivityReferenceSelector
        label="References (Optional)"
        buyerId={activity?.buyerId || ''}
        value={references}
        onChange={setReferences}
        isDisabled={isLoading}
        contacts={contacts}
      />
    </DillModal>
  );
}

export default EditCallModal;
